import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Link as RouterLink, withRouter } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { DefinitionList, Notice, PageContainer, PageSection, ToneChip } from '../../../../ui';
import actions from '../../../../redux/actions';
import { OperationStatus } from '../../../../types/OperationStatus';
import { DevicePlatform } from '../../../../types/DevicePlatform';
import { TestEnv } from '../../../../types/TestingEnvironment';
import { UserStatus } from '../../../../types/UserStatus';
import config from '../../../../config';

const mapStateToProps = (state) => {
  return {
    user: state.user,
    devices: state.devices,
    browsers: state.browsers,
    cycles: state.cycles,
    cycleAcceptStatus: state.cycleAcceptStatus,
    device: state.resolution.device,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    applyForCycle: (cycleId, testerBid) => dispatch(actions.applyForCycle(cycleId, testerBid)),
  };
};

type RequiredTestersPerPlatform = {
  id: number;
  name: string;
};

type TesterDevice = {
  name: string;
  platformId: string;
  manufacturerId: number;
  modelId: number;
  osId: number;
};

type TesterBrowser = {
  platformId: string;
  browserId: string;
  browserVersionId: string;
};

type CycleApplyState = {
  cycle: any;
  cycleId: number;
  suitableDeviceNames: string[];
  testerBid: string;
  errors: Set<string>;
};

class CycleApply extends Component<any> {
  public state: CycleApplyState;

  private requiredPlatforms: TestEnv[] = [];
  private requiredDevices: TestEnv[] = [];

  constructor(props) {
    super(props);

    this.state = {
      cycle: {},
      cycleId: parseInt(props.match.params.id),
      suitableDeviceNames: [],
      testerBid: '',
      errors: new Set(),
    };
  }

  componentDidMount() {
    if (!this.props.cycles.length) {
      return;
    }

    const cycle = this.props.cycles.find((el) => el.id === this.state.cycleId);

    // TODO DRY with getSuitableDeviceNames
    const nonPlatformEnvPlatformIds = cycle.testEnvs.reduce((result, el) => {
      result.add(el.platformId);
      return result;
    }, new Set());

    this.requiredPlatforms = cycle.testEnvs.filter((el) => {
      if (el.type === 'Platform' && !nonPlatformEnvPlatformIds.has(el.platformId)) {
        return true;
      }

      return false;
    });

    this.requiredDevices = cycle.testEnvs.filter((el) => el.type !== 'Platform');

    const suitableDeviceNames = this.getSuitableDeviceNames(
      cycle.testEnvs,
      this.props.user.devices,
      this.props.user.browsers
    );

    this.setState({
      cycle,
      suitableDeviceNames,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(this.props.cycles, prevProps.cycles)) {
      const cycle = this.props.cycles.find((el) => el.id === this.state.cycleId);
      this.setState({ cycle });
    }

    if (this.props.cycleAcceptStatus.status === OperationStatus.success) {
      this.props.history.push('/account/tester');
    }

    if (
      !_.isEqual(prevProps.user.devices, this.props.user.devices) ||
      !_.isEqual(prevProps.user.browsers, this.props.user.browsers) ||
      !_.isEqual(prevState.cycle, this.state.cycle)
    ) {
      const suitableDeviceNames = this.getSuitableDeviceNames(
        this.state.cycle.testEnvs,
        this.props.user.devices,
        this.props.user.browsers
      );

      this.setState({
        ...this.state,
        suitableDeviceNames,
      });
    }
  }

  onTesterBidUpdate = (event, data) => {
    this.setState({
      testerBid: data.value,
      errors: new Set(),
    });
  };

  onApply = () => {
    if (
      this.props.user.status === UserStatus.EvalCyclePending &&
      this.state.cycleId === config.evalCycleId
    ) {
      this.props.applyForCycle(this.state.cycle.id, 0);
      return;
    }

    if (this.state.testerBid === '') {
      this.setState({
        errors: new Set(["Field can't be empty"]),
      });

      return;
    }

    const testerBid: any = Number(this.state.testerBid);

    if (!Number.isInteger(testerBid)) {
      this.setState({
        errors: new Set(['Bid should be an integer']),
      });

      return;
    }

    if (isNaN(testerBid)) {
      this.setState({
        errors: new Set(['Bid should be a integer']),
      });

      return;
    }

    if (!testerBid) {
      this.setState({
        errors: new Set(['Please choose your bid']),
      });

      return;
    }

    if (testerBid < 0) {
      this.setState({
        errors: new Set(["Bid cani't be negative"]),
      });

      return;
    }

    if (testerBid > this.state.cycle.hourlyRate) {
      this.setState({
        errors: new Set([`Maximal bid allowed for this cycles is $${this.state.cycle.hourlyRate}`]),
      });

      return;
    }

    if (!this.state.errors.size) {
      this.props.applyForCycle(this.state.cycle.id, this.state.testerBid);
    }
  };

  getSuitableDeviceNames = (
    requiredTestEnvs: TestEnv[],
    testerDevices: TesterDevice[] = [],
    testerBrowsers: TesterBrowser[] = []
  ): string[] => {
    const nonPlatformEnvPlatformIds = requiredTestEnvs.reduce((result, el) => {
      if (el.type !== 'Platform') {
        result.add(el.platformId);
      }

      return result;
    }, new Set());

    const requiredDevices = requiredTestEnvs.filter((el) => el.type === 'MobileDevice');
    const requiredBrowsers = requiredTestEnvs.filter((el) => el.type === 'DesktopBrowser');
    const requiredPlatforms = requiredTestEnvs.filter((el) => {
      return el.type === 'Platform' && !nonPlatformEnvPlatformIds.has(el.platformId);
    });

    const suitableDeviceNames: Set<string> = new Set();

    for (const testerDevice of testerDevices) {
      if (
        requiredPlatforms.find((el) => el.platformId === testerDevice.platformId) ||
        requiredDevices.find((el) => (el as any).modelId === testerDevice.modelId)
      ) {
        const name = this.props.devices.models.find((el) => el.id === testerDevice.modelId).name;
        suitableDeviceNames.add(name);
      }
    }

    for (const testerBrowser of testerBrowsers) {
      if (requiredBrowsers.find((el) => (el as any).browserId === testerBrowser.browserId)) {
        const browserName = this.props.browsers.names.find(
          (el) => el.id === testerBrowser.browserId
        ).name;
        const browserVersion = this.props.browsers.versions.find(
          (el) => el.id === testerBrowser.browserVersionId
        ).version;
        suitableDeviceNames.add(`${browserName} ${browserVersion}`);
      }
    }

    if (requiredPlatforms.find((el) => el.platformId === DevicePlatform.Desktop)) {
      for (const testerBrowser of testerBrowsers) {
        const browserName = this.props.browsers.names.find(
          (el) => el.id === testerBrowser.browserId
        ).name;
        const browserVersion = this.props.browsers.versions.find(
          (el) => el.id === testerBrowser.browserVersionId
        ).version;
        suitableDeviceNames.add(`${browserName} ${browserVersion}`);
      }
    }

    return Array.from(suitableDeviceNames);
  };

  render() {
    const cycle = this.state.cycle;

    if (!Object.keys(cycle).length) {
      return null;
    }

    const suitableDeviceNames = this.state.suitableDeviceNames;

    const applyDisabled =
      suitableDeviceNames.length === 0 ||
      this.props.cycleAcceptStatus.status === OperationStatus.processing;

    const applyLoading = this.props.cycleAcceptStatus.status === OperationStatus.processing;

    const isMobile = this.props.device === 'mobile' || this.props.device === 'mobile-tablet';

    const nameList = (names: string[]) => (
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {names.map((name, i) => <li key={i}>{name}</li>)}
      </Box>
    );

    const isEvalCycleApplication = this.props.user.status === UserStatus.EvalCyclePending && !cycle.testerStatus;
    const error = this.state.errors.size > 0 ? this.state.errors.values().next().value : '';

    return (
      <PageContainer isMobile={isMobile}>
        {(this.requiredPlatforms.length > 0 || this.requiredDevices.length > 0) && (
          <PageSection title="The customer requested" padding={isMobile ? 1.5 : 2.5}>
            <DefinitionList
              isMobile={isMobile}
              items={[
                this.requiredPlatforms.length > 0 && { label: 'Platforms', value: nameList(this.requiredPlatforms.map((el) => el.name)) },
                this.requiredDevices.length > 0 && { label: 'Devices', value: nameList(this.requiredDevices.map((el) => el.name)) },
              ]}
            />
          </PageSection>
        )}

        <PageSection
          title="Your applicable devices"
          padding={isMobile ? 1.5 : 2.5}
          actions={
            <Button variant="outlined" size="small" startIcon={<AddIcon />} component={RouterLink} to="/account/tester/myprofile/devices">
              Add more devices
            </Button>
          }
        >
          {suitableDeviceNames.length ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {suitableDeviceNames.map((name, i) => <ToneChip key={i} label={name} tone="primary" />)}
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Notice severity="error">You don&apos;t have any devices applicable for this cycle</Notice>
            </Box>
          )}
          <Typography variant="body2" color="textSecondary">
            Any of your listed devices can be chosen. Make sure your profile is up to date.
          </Typography>
        </PageSection>

        <PageSection title="Accept this cycle" padding={isMobile ? 1.5 : 2.5}>
          {error && (
            <Box sx={{ mb: 2 }}>
              <Notice severity="error">{error}</Notice>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {isEvalCycleApplication ? (
              <Button variant="contained" size="large" endIcon={applyLoading ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
                onClick={this.onApply} disabled={applyDisabled}>
                Accept this cycle
              </Button>
            ) : cycle.testerStatus && cycle.testerBid ? (
              <Button variant="contained" size="large" endIcon={<CheckIcon />} disabled>
                {`You bid $${cycle.testerBid} already`}
              </Button>
            ) : (
              <>
                <TextField
                  name="testerBid"
                  label="My bid (per hour)"
                  value={this.state.testerBid}
                  onChange={(event) => this.onTesterBidUpdate(event, { value: event.target.value })}
                  error={!!error}
                  helperText={`Up to ${cycle.hourlyRate}`}
                  FormHelperTextProps={{ sx: { color: 'text.secondary' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ width: 200 }}
                />
                <Button variant="contained" size="large" endIcon={<CheckIcon />} onClick={this.onApply}
                  disabled={suitableDeviceNames.length === 0 || this.state.errors.size > 0} sx={{ mt: 0.5 }}>
                  Accept this cycle
                </Button>
              </>
            )}
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            By accepting I <Link component={RouterLink} to="/terms">agree to the terms</Link>
          </Typography>
        </PageSection>
      </PageContainer>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CycleApply));
